
/**
 * Simulate resilient operations for a 24-hour window starting at a specific hour of a specific day.
 * 
 * @param {Object} results - The full optimization results object
 * @param {number} dayIndex - The day index (0-364)
 * @param {number} startHourIndex - The hour of the day to start the outage (0-23)
 * @param {number} tph - Time steps per hour (default 1)
 * @returns {Object} { data: [], isOffGrid: true, infeasibilityMetrics: ... }
 */
export function simulateDailyResilience(results, dayIndex, startHourIndex = 0, tph = 1) {
    if (!results) return { data: [], isOffGrid: true };

    const outputs = results.outputs || {};

    // Extract Critical Load Series - NO FALLBACKS
    const critical_series = outputs?.ElectricLoad?.critical_load_series_kw;
    const regular_load_series = outputs?.ElectricLoad?.load_series_kw;

    if (!critical_series || critical_series.length === 0) {
        if (!regular_load_series || regular_load_series.length === 0) {
            throw new Error('Resilience simulation requires load data. No critical_load_series_kw or load_series_kw found in results.');
        }
        console.warn('[RESILIENCE] No critical_load_series_kw found - using load_series_kw. Ensure critical load is properly configured if needed.');
    }

    // Use critical if available, otherwise regular load with warning
    const load_to_use = critical_series && critical_series.length > 0 ? critical_series : regular_load_series;

    // Helper to extract series with fallback keys
    const getSeries = (obj, keys) => {
        if (!obj) return [];
        for (const k of keys) {
            if (Array.isArray(obj[k]) && obj[k].length > 0) return obj[k];
        }
        return [];
    };

    // Extract PV Series - try total production first, then reconstruct from components
    let pv_series = getSeries(outputs?.PV, ['year_one_power_production_series_kw']);

    // If missing, try to reconstruct from components (PV to Load + PV to Battery + PV to Grid)
    if (pv_series.length === 0) {
        console.warn('[RESILIENCE] No year_one_power_production_series_kw found - attempting to reconstruct from components');
        const pv_load = getSeries(outputs?.PV, ['electric_to_load_series_kw', 'pv_to_load_series_kw']);
        const pv_batt = getSeries(outputs?.PV, ['electric_to_storage_series_kw', 'pv_to_storage_series_kw', 'to_storage_series_kw']);
        const pv_grid = getSeries(outputs?.PV, ['electric_to_grid_series_kw', 'electric_export_to_grid_series_kw', 'to_grid_series_kw']);

        if (pv_load.length > 0) {
            pv_series = pv_load.map((v, i) => (v || 0) + (pv_batt[i] || 0) + (pv_grid[i] || 0));
            console.info('[RESILIENCE] Successfully reconstructed PV series from component flows');
        } else if (outputs?.PV) {
            // PV is configured but no series data available
            throw new Error('Resilience simulation: PV is configured but no production data found. Cannot simulate without PV series data.');
        }
        // If no PV at all, pv_series stays empty (no PV in system)
    }

    // Extract Battery Specs
    const battery_kwh = outputs?.ElectricStorage?.size_kwh || 0;
    const battery_kw = outputs?.ElectricStorage?.size_kw || 0;
    // Initial SOC at start of outage - ideally we'd take this from the simulated operation
    // For now, let's assume it starts at the SOC derived from the main simulation at that hour, 
    // or full if not available.
    const full_soc_series = outputs?.ElectricStorage?.soc_series_fraction || [];

    // Determine the absolute start index in the 8760 array
    const stepsPerDay = 24 * tph; // 24
    const startOfDayIndex = dayIndex * stepsPerDay; // e.g. 0 for day 0
    const outageStartIndex = startOfDayIndex + startHourIndex; // e.g. 14 for 2pm

    // Simulation Parameters
    const min_soc = outputs?.ElectricStorage?.soc_min_fraction || 0.2; // 20% min SOC usually
    const inverter_eff = 0.96; // Assumed
    const battery_eff = 0.96; // Round trip is usually ~92%, so one way ~96%

    let current_stored_kwh = 0;

    // Initialize SOC from the main simulation if available - NO FALLBACKS
    if (full_soc_series[outageStartIndex] !== undefined) {
        current_stored_kwh = full_soc_series[outageStartIndex] * battery_kwh;
    } else if (battery_kwh > 0) {
        // Battery is configured but no SOC series - this shouldn't happen
        console.warn(`[RESILIENCE] Battery configured (${battery_kwh} kWh) but no SOC series found at index ${outageStartIndex}. Assuming 100% SOC (best-case scenario). This may indicate incomplete optimization results.`);
        current_stored_kwh = battery_kwh * 1.0; // Best-case assumption with warning
    }

    const data = [];
    const simulatedHours = 24;

    // Base timestamp for the start of the simulation window
    // Logic from reoptToDailySeries: base is Jan 1
    const year = results?.inputs?.Scenario?.analysis_year || new Date().getFullYear();
    const baseTimestamp = new Date(year, 0, 1).getTime(); // Jan 1 00:00

    // total steps = 24 * tph
    // We iterate 24 hours * tph steps
    const simulationSteps = 24 * tph;

    for (let i = 0; i <= simulationSteps; i++) {
        // Wrap around index if it goes past end of year? (unlikely for standard view but good practice)
        // Actually, we want a rolling window. If start is Dec 31 18:00, we go into Jan 1 next year.
        // For simplicity, let's wrap the index modulo 8760.
        const globalIdx = (outageStartIndex + i) % 8760;

        const load_kw = load_to_use[globalIdx] || 0;
        const pv_kw = pv_series[globalIdx] || 0;

        // Dispatch Logic:
        // 1. PV serves Load
        let pv_to_load = Math.min(pv_kw, load_kw);
        let remaining_load = load_kw - pv_to_load;
        let excess_pv = pv_kw - pv_to_load;

        // 2. Excess PV charges Battery
        let pv_to_bess = 0;
        if (excess_pv > 0) {
            // How much room is in battery?
            const room_kwh = battery_kwh - current_stored_kwh;
            // Max charge power
            const max_charge = battery_kw;

            // Allowable charge based on power and capacity
            const allowable_charge = Math.min(excess_pv, max_charge, room_kwh);

            pv_to_bess = allowable_charge;
            // Update storage (apply efficiency?)
            // Usually charge efficiency applies here.
            current_stored_kwh += pv_to_bess * Math.sqrt(battery_eff); // approx
        }

        // 3. Battery serves Remaining Load
        let bess_to_load = 0;
        if (remaining_load > 0) {
            // How much energy available?
            const available_kwh = Math.max(0, current_stored_kwh - (battery_kwh * min_soc));
            // Max discharge power
            const max_discharge = battery_kw;

            // Needed discharge
            // Discharge efficiency: we need X, so we pull X / eff from battery
            const needed_discharge = remaining_load; // kW

            // Can we support this?
            // Energy limit:
            const possible_energy_discharge = available_kwh * Math.sqrt(battery_eff); // kWh available at output

            const limit = Math.min(max_discharge, possible_energy_discharge);

            bess_to_load = Math.min(needed_discharge, limit);

            // Remove from storage
            current_stored_kwh -= bess_to_load / Math.sqrt(battery_eff);

            remaining_load -= bess_to_load;
        }

        // 4. Utility is dead
        let utility_to_load = 0;

        // 5. Unmet Load (Diesel/Gen would act here if present, assume none for "simple" resilience check or simple logic)
        // If we want to support generator, we add it here.
        // Let's assume generic generator exists if in output
        const hasGen = (outputs?.Generator?.size_kw || 0) > 0;
        let gen_to_load = 0;
        if (hasGen && remaining_load > 0) {
            const gen_capacity = outputs.Generator.size_kw;
            gen_to_load = Math.min(remaining_load, gen_capacity);
            remaining_load -= gen_to_load;
        }

        // Residual is unmet
        const utility_residual = remaining_load;

        // Current SOC %
        const soc_pct = current_stored_kwh / battery_kwh;

        // Calculate timestamp for this step
        // Start Time = base + (outageStartIndex * 3600 * 1000)
        // Current Time = Start Time + (i * 3600 * 1000)
        // NOTE: outageStartIndex is global hour.
        const tickTimestamp = baseTimestamp + ((outageStartIndex + i) * 3600 * 1000);

        data.push({
            timestamp: tickTimestamp,
            hour: (startHourIndex + i) % 24, // Local hour for display if needed
            load: load_kw,
            utility_residual: utility_residual, // This will show as 'Net Utility' or specific Unmet color
            utility_to_load: 0,
            bess_to_load: bess_to_load,
            diesel_to_load: gen_to_load,
            solar_to_bess: pv_to_bess,
            utility_to_bess: 0,
            gen_to_bess: 0,
            // PowerGraph keys
            bess_discharge: bess_to_load,
            genset: gen_to_load,
            bess_charge_solar: -pv_to_bess, // Negative for stacking below axis
            bess_charge_grid: 0,
            bess_charge_gen: 0,
            solar_to_load: pv_to_load,
            solar_export: 0, // Grid is down
            bess_export: 0,
            diesel_export: 0,
            soc_pct: soc_pct
        });
    }

    return {
        data,
        isOffGrid: true, // Forces graph to show Unmet Load logic usually
        infeasibilityMetrics: {
            hasUnmetLoad: data.some(d => d.utility_residual > 0.1),
            totalUnmetLoad: data.reduce((acc, d) => acc + d.utility_residual, 0)
        }
    };
}
